import type { MatchClient } from "./match-client";
import { ArenaCanvas } from "./ArenaCanvas";
import type { Dict, Locale } from "./i18n";
import { personaName } from "./i18n";
import { slotTheme } from "./theme";

/**
 * A small view of the arena drawn through one cricket's eyes.
 *
 * This is the part of the design that makes the game legible. A broadcast view
 * shows everything, which is fine for watching but teaches nothing about *why*
 * a cricket did anything: two crickets wander, occasionally collide, and the
 * score moves for reasons the viewer cannot see. With this beside the broadcast,
 * the difference between what is happening and what a cricket *knows* is on
 * screen at the same time. That gap - a trap sitting in plain sight as an
 * untouched crystal, an opponent two units outside its vision radius - is the
 * actual game.
 *
 * It is deliberately a separate render rather than a mask over the broadcast.
 * Masking one view would make the broadcast switch identity depending on a
 * toggle, and a viewer would lose the thing this panel exists to show.
 *
 * Placed in the rail on the cricket's own side of the screen, so which eyes you
 * are looking through is answered by position rather than by a label.
 *
 * The render is `readOnly`: it shares its client with the broadcast, and two
 * canvases each stepping the same world would run the match at double speed and
 * desynchronise the two views.
 */
export function VisionPanel({
  client,
  locale,
  d,
  slot,
}: {
  client: MatchClient;
  locale: Locale;
  d: Dict;
  /** Whose eyes. Fixed per rail, not a toggle. */
  slot: 0 | 1;
}) {
  const theme = slotTheme(slot);
  const who = personaName(client.personaOf(client.infos[slot].id), locale);

  return (
    <div className="panel vision-inset">
      <div className="panel-head" style={{ borderColor: theme.dim }}>
        <span>{d.visionPanel}</span>
        {/* Naming the viewpoint in the header, not just the frame. A masked view
            with no label is easy to mistake for a rendering bug. */}
        <span className="mono" style={{ color: theme.color, fontSize: 9 }}>
          {who}
        </span>
      </div>

      <div className="vision-frame">
        <ArenaCanvas client={client} speed={1} paused d={d} readOnly visionSlot={slot} />
      </div>

      <p className="vision-caption">{d.visionPanelHint}</p>
    </div>
  );
}
